<script setup lang="ts">
/**
 * 既存の物件を物件マスタへ一括で紐付ける。
 *
 * 紐付けが入ると種別が「物件マスタ (紐付け済み)」で解決されるようになり、
 * 名前からの推定に頼らなくなる。マスタ側で種別を直せば全物件に効く。
 *
 * 名前が一意に決まるものだけを対象にする。同名の別ビルが実在するため
 * (協和ビル = 台東区上野 / 千代田区六番町)、複数該当は「要確認」として
 * 一覧に出すだけで自動では触らない — 誤紐付けは後から気づけない。
 */
import { computed, ref } from 'vue';
import { useBuildingStore } from '@/stores/buildingStore';
import { usePropertyStore } from '@/stores/propertyStore';
import { usePropertyStoreV2 } from '@/stores/propertyStoreV2';
import { planPropertyLinks, type LinkTarget } from '@/lib/building/link-properties';
import { buildingAddress } from '@/types/building';

const open = defineModel<boolean>('open', { default: false });

const buildings = useBuildingStore();
const v1 = usePropertyStore();
const v2 = usePropertyStoreV2();
const applied = ref<number | null>(null);

/** V1/V2 の物件を共通の形に落とす (どちらのストアかを覚えておく)。 */
interface Row extends LinkTarget {
  source: 'v1' | 'v2';
}

const targets = computed<Row[]>(() => [
  ...v1.properties.map((p) => ({
    id: p.id,
    name: p.name,
    hubspotPropertyId: p.hubspotPropertyId,
    source: 'v1' as const,
  })),
  ...v2.properties.map((p) => ({
    id: p.id,
    name: p.buildingName.ja,
    hubspotPropertyId: p.hubspotPropertyId,
    source: 'v2' as const,
  })),
]);

const plan = computed(() =>
  planPropertyLinks(targets.value, buildings.entries),
);

function apply(): void {
  let n = 0;
  for (const { target, entry } of plan.value.toLink) {
    const row = target as Row;
    const store = row.source === 'v1' ? v1 : v2;
    store.updateProperty(row.id, { hubspotPropertyId: entry.id });
    n += 1;
  }
  applied.value = n;
}

function close(): void {
  applied.value = null;
  open.value = false;
}
</script>

<template>
  <v-dialog
    v-model="open"
    max-width="720"
  >
    <v-card>
      <v-card-title>物件マスタへの紐付け</v-card-title>
      <v-card-text>
        <p class="hint">
          物件名がマスタと一致する物件に <code>hubspotPropertyId</code> を設定します。
          紐付くと物件種別がマスタから解決され、名前からの推定に頼らなくなります。
        </p>

        <p
          v-if="buildings.entries.length === 0"
          class="warn"
        >
          物件マスタが空です。先に「物件マスタを取り込む」を実行してください。
        </p>

        <template v-else-if="applied === null">
          <v-table density="compact">
            <tbody>
              <tr>
                <td>紐付けられる (名前が一意)</td>
                <td class="num strong">
                  {{ plan.toLink.length }}
                </td>
              </tr>
              <tr>
                <td>既に紐付け済み</td>
                <td class="num">
                  {{ plan.alreadyLinked.length }}
                </td>
              </tr>
              <tr>
                <td>★要確認 (同名がマスタに複数)</td>
                <td class="num">
                  {{ plan.ambiguous.length }}
                </td>
              </tr>
              <tr>
                <td>マスタに見つからない</td>
                <td class="num">
                  {{ plan.unmatched.length }}
                </td>
              </tr>
            </tbody>
          </v-table>

          <!-- 要確認は自動で触らないので、どれを手で直すべきか見せる。 -->
          <div
            v-if="plan.ambiguous.length > 0"
            class="detail"
          >
            <div class="detail-head">
              要確認 (手動で選んでください)
            </div>
            <div
              v-for="a in plan.ambiguous.slice(0, 8)"
              :key="a.target.id"
              class="detail-row"
            >
              <strong>{{ a.target.name }}</strong>
              <span
                v-for="c in a.candidates"
                :key="c.id"
                class="cand"
              >{{ buildingAddress(c) || '(住所未登録)' }}</span>
            </div>
            <div
              v-if="plan.ambiguous.length > 8"
              class="detail-more"
            >
              ほか {{ plan.ambiguous.length - 8 }} 件
            </div>
          </div>
        </template>

        <p
          v-else
          class="done"
        >
          {{ applied }} 件を紐付けました。
        </p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="close">
          閉じる
        </v-btn>
        <v-btn
          v-if="applied === null"
          color="primary"
          variant="flat"
          :disabled="plan.toLink.length === 0"
          @click="apply"
        >
          {{ plan.toLink.length }} 件を紐付ける
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.hint {
  font-size: var(--ds-font-xs);
  color: var(--ds-text-secondary);
  margin-bottom: var(--ds-space-sm);
}

.warn {
  font-size: var(--ds-font-sm);
  color: rgb(var(--v-theme-warning));
}

.done {
  font-size: var(--ds-font-sm);
}

.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.strong {
  font-weight: 700;
}

.detail {
  margin-top: var(--ds-space-sm);
  font-size: var(--ds-font-xs);
}

.detail-head {
  color: var(--ds-text-secondary);
  margin-bottom: var(--ds-space-xxs);
}

.detail-row {
  padding: 2px 0;
  border-top: 1px solid var(--ds-border);
}

.cand {
  margin-left: var(--ds-space-xs);
  color: var(--ds-text-secondary);
}

.detail-more {
  margin-top: var(--ds-space-xxs);
  color: var(--ds-text-secondary);
}
</style>
